import { useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link as RouterLink } from 'react-router';
import { AdminListPageHeader, adminDataTableSx, dataTableCardSx } from '../components/admin/adminListPageUi';
import {
  adminConsoleAuthApi,
  adminConsoleGroupsApi,
  type AdminConsoleGroup,
} from '../api/adminConsole';
import { ADMIN_CONSOLE_ME_QUERY_KEY } from '../lib/adminSession';
import { messageFromAdminConsoleAxiosError } from '../lib/axiosErrorMessage';
import { getAdminConsoleToken } from '../lib/adminConsoleToken';
import {
  applyPermissionGroupToggle,
  filterAssignablePermissions,
  groupCatalogBySection,
  nextAdminGroupSort,
  permissionGroupSelection,
  sortAdminGroups,
  type AdminGroupSortDir,
  type AdminGroupSortKey,
} from '../lib/adminGroupsTable';

type GroupForm = {
  name: string;
  description: string;
  permissions: string[];
};

const emptyForm: GroupForm = { name: '', description: '', permissions: [] };

function memberInitials(name: string, email: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  if (parts[0]?.[0]) return parts[0][0].toUpperCase();
  return (email[0] ?? '?').toUpperCase();
}

export function AdminGroupsPage() {
  const theme = useTheme();
  const qc = useQueryClient();
  const hasToken = typeof window !== 'undefined' && !!getAdminConsoleToken();
  const listQ = useQuery({
    queryKey: ['adminConsoleGroups'],
    queryFn: () => adminConsoleGroupsApi.list(),
    enabled: hasToken,
  });
  const catalogQ = useQuery({
    queryKey: ['adminConsolePermissionCatalog'],
    queryFn: () => adminConsoleGroupsApi.catalog(),
    enabled: hasToken,
  });

  const meQ = useQuery({
    queryKey: ADMIN_CONSOLE_ME_QUERY_KEY,
    queryFn: () => adminConsoleAuthApi.me(),
    enabled: hasToken,
  });
  // RBAC Phase 1: once roles decide access (v2), groups are kept read-only for reference.
  const readOnly = meQ.data?.authzModel === 'v2';

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<AdminConsoleGroup | null>(null);
  const [detail, setDetail] = useState<AdminConsoleGroup | null>(null);
  const [detailLoadingId, setDetailLoadingId] = useState<string | null>(null);
  const [form, setForm] = useState<GroupForm>(emptyForm);
  const [sortKey, setSortKey] = useState<AdminGroupSortKey>('name');
  const [sortDir, setSortDir] = useState<AdminGroupSortDir>('asc');

  const groupedCatalog = useMemo(
    () => groupCatalogBySection(catalogQ.data?.items ?? []),
    [catalogQ.data],
  );
  const groups = listQ.data?.items ?? [];
  const sortedGroups = useMemo(
    () => sortAdminGroups(groups, sortKey, sortDir),
    [groups, sortKey, sortDir],
  );

  const save = useMutation({
    mutationFn: async () => {
      if (editing) {
        return adminConsoleGroupsApi.patch(editing.id, form);
      }
      return adminConsoleGroupsApi.create(form);
    },
    onSuccess: async () => {
      setFormOpen(false);
      setEditing(null);
      setForm(emptyForm);
      await qc.invalidateQueries({ queryKey: ['adminConsoleGroups'] });
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => adminConsoleGroupsApi.remove(id),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['adminConsoleGroups'] });
    },
  });

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setFormOpen(true);
  };

  const openEdit = (group: AdminConsoleGroup) => {
    setEditing(group);
    setForm({
      name: group.name,
      description: group.description,
      permissions: filterAssignablePermissions(group.permissions, catalogQ.data?.items ?? []),
    });
    setFormOpen(true);
  };

  const togglePermission = (key: string, checked: boolean) => {
    setForm((prev) => ({
      ...prev,
      permissions: checked
        ? [...new Set([...prev.permissions, key])]
        : prev.permissions.filter((k) => k !== key),
    }));
  };

  const toggleSort = (key: AdminGroupSortKey) => {
    const next = nextAdminGroupSort(sortKey, sortDir, key);
    setSortKey(next.key);
    setSortDir(next.dir);
  };

  const renderSortLabel = (key: AdminGroupSortKey, label: string) => (
    <TableSortLabel
      hideSortIcon={false}
      active={sortKey === key}
      direction={sortKey === key ? sortDir : 'asc'}
      onClick={() => toggleSort(key)}
    >
      {label}
    </TableSortLabel>
  );

  if (!hasToken) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="warning">Sign in to manage groups.</Alert>
      </Box>
    );
  }

  if (listQ.isPending) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (listQ.isError) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">{messageFromAdminConsoleAxiosError(listQ.error)}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <AdminListPageHeader
        title="Groups"
        subtitle="Assign tabs and actions to groups, then put employees in those groups."
        endAdornment={
          <>
            <Chip label={`${listQ.data?.total ?? 0} groups`} size="small" />
            <Button variant="contained" size="small" onClick={openCreate} disabled={readOnly}>
              Create group
            </Button>
          </>
        }
      />

      {readOnly && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Access is now decided by roles. Groups no longer grant tabs or actions and are read-only here;
          assign roles from the Users page.{' '}
          <Button component={RouterLink} to="/admin/roles" size="small" sx={{ ml: 0.5 }}>
            Go to Roles
          </Button>
        </Alert>
      )}

      {(save.isError || remove.isError) && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {messageFromAdminConsoleAxiosError(save.error ?? remove.error)}
        </Alert>
      )}

      <Card variant="outlined" sx={dataTableCardSx(theme)}>
        <Table size="small" stickyHeader sx={adminDataTableSx(theme)}>
          <TableHead>
            <TableRow>
              <TableCell sortDirection={sortKey === 'name' ? sortDir : false}>
                {renderSortLabel('name', 'Name')}
              </TableCell>
              <TableCell sortDirection={sortKey === 'members' ? sortDir : false} sx={{ width: 140 }}>
                {renderSortLabel('members', 'Members')}
              </TableCell>
              <TableCell sortDirection={sortKey === 'permissions' ? sortDir : false} sx={{ width: 160 }}>
                {renderSortLabel('permissions', 'Permissions')}
              </TableCell>
              <TableCell align="right" sx={{ width: 260 }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedGroups.map((g) => {
              const deleteBlocked = readOnly || g.isSystem || (g.memberCount ?? 0) > 0;
              return (
                <TableRow key={g.id} hover>
                  <TableCell sx={{ maxWidth: 420 }}>
                    <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap" useFlexGap>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {g.name}
                      </Typography>
                      {g.isSystem && (
                        <Chip label="System" size="small" variant="outlined" color="primary" />
                      )}
                    </Stack>
                    {g.description && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                        {g.description}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`${g.memberCount ?? 0} ${(g.memberCount ?? 0) === 1 ? 'member' : 'members'}`}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      color={g.permissions.length > 0 ? 'primary' : 'default'}
                      variant={g.permissions.length > 0 ? 'filled' : 'outlined'}
                      label={`${g.permissions.length} ${g.permissions.length === 1 ? 'permission' : 'permissions'}`}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" gap={0.5} justifyContent="flex-end">
                      <Button
                        size="small"
                        disabled={detailLoadingId === g.id}
                        onClick={async () => {
                          setDetailLoadingId(g.id);
                          try {
                            const full = await adminConsoleGroupsApi.get(g.id);
                            setDetail(full);
                          } finally {
                            setDetailLoadingId(null);
                          }
                        }}
                      >
                        Members
                      </Button>
                      <Button size="small" onClick={() => openEdit(g)} disabled={readOnly}>
                        Edit
                      </Button>
                      <Tooltip
                        title={
                          readOnly
                            ? 'Groups are read-only while roles decide access'
                            : g.isSystem
                              ? 'System groups cannot be deleted'
                              : (g.memberCount ?? 0) > 0
                                ? 'Remove members before deleting this group'
                                : 'Delete group'
                        }
                      >
                        <span>
                          <Button
                            size="small"
                            color="error"
                            disabled={deleteBlocked}
                            onClick={() => remove.mutate(g.id)}
                          >
                            Delete
                          </Button>
                        </span>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>

      <Dialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ pb: 1.25 }}>
          <Typography variant="h6" component="span" sx={{ fontWeight: 700, display: 'block' }}>
            {editing ? 'Edit group' : 'Create group'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Choose which tabs and actions members of this group can use.
          </Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 2.5 }}>
          {save.isError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {messageFromAdminConsoleAxiosError(save.error)}
            </Alert>
          )}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 2,
              mb: 2.5,
            }}
          >
            <TextField
              label="Name"
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              size="small"
              fullWidth
              disabled={Boolean(editing?.isSystem)}
            />
            <TextField
              label="Description"
              value={form.description}
              onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
              size="small"
              fullWidth
            />
          </Box>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Permissions
            </Typography>
            <Chip size="small" label={`${form.permissions.length} selected`} />
          </Stack>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 1.5,
            }}
          >
            {groupedCatalog.map(([groupName, items]) => {
              const keys = items.map((item) => item.key);
              const selection = permissionGroupSelection(form.permissions, keys);
              return (
                <Box
                  key={groupName}
                  sx={{
                    p: 1.5,
                    borderRadius: 1.5,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'light' ? 0.03 : 0.08),
                  }}
                >
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      {groupName}
                    </Typography>
                    <FormControlLabel
                      sx={{ mr: 0 }}
                      control={
                        <Checkbox
                          size="small"
                          checked={selection === 'all'}
                          indeterminate={selection === 'some'}
                          onChange={(_, checked) =>
                            setForm((prev) => ({
                              ...prev,
                              permissions: applyPermissionGroupToggle(prev.permissions, keys, checked),
                            }))
                          }
                        />
                      }
                      label={
                        <Typography variant="caption" color="text.secondary">
                          All
                        </Typography>
                      }
                    />
                  </Stack>
                  <FormGroup sx={{ gap: 0.25 }}>
                    {items.map((item) => (
                      <FormControlLabel
                        key={item.key}
                        sx={{ alignItems: 'flex-start', ml: 0, mr: 0 }}
                        control={
                          <Checkbox
                            size="small"
                            sx={{ pt: 0.25 }}
                            checked={form.permissions.includes(item.key)}
                            onChange={(_, checked) => togglePermission(item.key, checked)}
                          />
                        }
                        label={
                          <Typography variant="body2" sx={{ pt: 0.35 }}>
                            {item.label}
                          </Typography>
                        }
                      />
                    ))}
                  </FormGroup>
                </Box>
              );
            })}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button onClick={() => setFormOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={readOnly || save.isPending || !form.name.trim()} onClick={() => save.mutate()}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(detail)}
        onClose={() => setDetail(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ pb: 1.25 }}>
          <Typography variant="h6" component="span" sx={{ fontWeight: 700, display: 'block' }}>
            {detail?.name} members
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Employees assigned to this group from the Users page.
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {(detail?.members ?? []).length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
              No employees in this group yet. Assign them from the Users page.
            </Typography>
          ) : (
            <List disablePadding>
              {(detail?.members ?? []).map((m) => (
                <ListItem key={m.id} disableGutters sx={{ py: 1, alignItems: 'flex-start' }}>
                  <ListItemAvatar>
                    <Avatar sx={{ width: 36, height: 36, fontSize: '0.85rem', bgcolor: 'primary.main' }}>
                      {memberInitials(m.displayName, m.email)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {m.displayName}
                        </Typography>
                        <Chip
                          size="small"
                          label={m.isActive ? 'Active' : 'Inactive'}
                          color={m.isActive ? 'success' : 'default'}
                          variant={m.isActive ? 'filled' : 'outlined'}
                        />
                      </Stack>
                    }
                    secondary={
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}
                      >
                        {m.email}
                      </Typography>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button onClick={() => setDetail(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
